import { Button } from "@component/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@component/dialog";
import { AlertTriangleIcon, Loader2Icon } from "lucide-react";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading?: boolean;
    title: string;
    description: React.ReactNode;
    confirmLabel: string;
}

/**
 * Confirm a hiring-flow gate resolution (#3186). These actions read as small
 * buttons but they overrule a screening condition HR configured and restart a
 * flow the gate deliberately paused, so they get the same confirm step as the
 * other consequential candidate actions.
 */
export default function FlowGateResolveConfirmDialog({
    isOpen,
    onClose,
    onConfirm,
    isLoading = false,
    title,
    description,
    confirmLabel,
}: Props) {
    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isLoading) onClose(); }}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                            <AlertTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-500" />
                        </div>
                        <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {title}
                        </DialogTitle>
                    </div>
                    <DialogDescription className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                        {description}
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-4 flex gap-3 justify-end">
                    <Button variant="outline" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {isLoading ? <Loader2Icon className="w-4 h-4 animate-spin" /> : null}
                        {confirmLabel}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
